import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type UserRole = "user" | "agent" | "admin";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  emirate: string | null;
  country: string | null;
  postal_code: string | null;
  avatar_url: string | null;
  is_online: boolean;
  marketing_opt_in: boolean;
  push_notifications_enabled: boolean;
}

export type InviteCodeError = 
  | 'CODE_REQUIRED' 
  | 'INVALID_CODE' 
  | 'CODE_USED' 
  | 'CODE_EXPIRED' 
  | 'CODE_REVOKED'
  | 'EMAIL_ALREADY_EXISTS' 
  | 'GENERIC';

interface SignUpResult {
  error: Error | null;
  codeError?: InviteCodeError;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  loading: boolean;
  isAgent: boolean;
  isAdmin: boolean;
  signUp: (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string,
    inviteCode: string,
    phone?: string,
    addressUae?: string
  ) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const lastOneSignalUserId = useRef<string | null>(null);

  // Sync user with OneSignal via deferred pattern (guaranteed after SDK init)
  useEffect(() => {
    if (user?.id && user.id !== lastOneSignalUserId.current) {
      lastOneSignalUserId.current = user.id;
      window.OneSignalDeferred?.push(async (OneSignal) => {
        try {
          console.log('[OneSignal] Syncing user:', user.id);
          await OneSignal.login(user.id);
        } catch (error) {
          console.error('[OneSignal] User sync error:', error);
        }
      });
    } else if (!user && lastOneSignalUserId.current) {
      lastOneSignalUserId.current = null;
      window.OneSignalDeferred?.push(async (OneSignal) => {
        try {
          console.log('[OneSignal] Logging out user');
          await OneSignal.logout();
        } catch (error) {
          console.error('[OneSignal] Logout error:', error);
        }
      });
    }
  }, [user]);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (!error && data) {
      setProfile(data as Profile);
    }
  };

  const fetchRoles = async (userId: string) => {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);

    if (!error && data) {
      setRoles(data.map((r) => r.role as UserRole));
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
      await fetchRoles(user.id);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Fetch roles synchronously before setting loading to false
          setTimeout(async () => {
            await Promise.all([
              fetchProfile(session.user.id),
              fetchRoles(session.user.id)
            ]);
            setLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await Promise.all([
          fetchProfile(session.user.id),
          fetchRoles(session.user.id)
        ]);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string,
    inviteCode: string,
    phone?: string,
    addressUae?: string
  ): Promise<SignUpResult> => {
    // Validate invite code is provided
    const trimmedCode = inviteCode?.trim();
    if (!trimmedCode) {
      return { error: new Error("Invite code is required"), codeError: 'CODE_REQUIRED' };
    }

    // Step 1: VALIDATE the invite code BEFORE creating the user
    const { data: validateResult, error: validateError } = await supabase.rpc('validate_invite_code', {
      p_code: trimmedCode
    });

    if (validateError) {
      return { error: new Error("Failed to validate invite code"), codeError: 'GENERIC' };
    }

    if (validateResult !== 'OK') {
      const errorCode = validateResult as InviteCodeError;
      const errorMessages: Record<string, string> = {
        'INVALID_CODE': 'Invalid invite code. Please check the code provided by the concierge.',
        'CODE_USED': 'This invite code has already been used. Please request a new code.',
        'CODE_EXPIRED': 'This invite code has expired. Please request a new code.',
        'CODE_REVOKED': 'This invite code has been revoked. Please request a new code.',
      };
      
      return { 
        error: new Error(errorMessages[errorCode] || 'Invalid invite code'), 
        codeError: errorCode 
      };
    }

    const redirectUrl = `${window.location.origin}/`;
    
    // Step 2: Create the Supabase Auth user (only if code is valid)
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      },
    });

    if (authError) {
      if (authError.message.includes("already registered") || authError.message.includes("already been registered")) {
        return { error: authError, codeError: 'EMAIL_ALREADY_EXISTS' };
      }
      return { error: authError, codeError: 'GENERIC' };
    }

    const userId = authData.user?.id;
    if (!userId) {
      return { error: new Error("Failed to create user"), codeError: 'GENERIC' };
    }

    // Step 3: Consume the invite code via RPC (now that user is created)
    const { data: codeResult, error: rpcError } = await supabase.rpc('consume_invite_code', {
      p_code: trimmedCode,
      p_user_id: userId
    });

    if (rpcError || codeResult !== 'OK') {
      // This should rarely happen since we validated first
      // But handle it gracefully
      console.error('Failed to consume invite code after user creation:', rpcError);
    }

    // Step 4: Update profile with additional info if provided
    if (phone || addressUae) {
      await supabase
        .from("profiles")
        .update({
          phone: phone || '+971',
          address_line1: addressUae || 'Dubai, UAE',
          city: 'Dubai',
          emirate: 'Dubai',
          country: 'United Arab Emirates',
        })
        .eq("id", userId);
    }

    return { error: null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return { error: new Error("Not authenticated") };

    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", user.id);

    if (!error) {
      setProfile((prev) => prev ? { ...prev, ...updates } : null);
    }

    return { error: error as Error | null };
  };

  const isAgent = roles.includes("agent") || roles.includes("admin");
  const isAdmin = roles.includes("admin");

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        loading,
        isAgent,
        isAdmin,
        signUp,
        signIn,
        signOut,
        updateProfile,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}